import Navbar from "@/components/Navbar";
import { useEffect, useState, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DollarSign, Calendar, CheckCircle, Clock, LogOut, ChevronDown, Calendar as CalendarIcon } from "lucide-react";

export default function AdminDashboard() {
  const nav = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("admin_token") : null;
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [tab, setTab] = useState("bookings");
  const [newCoupon, setNewCoupon] = useState({ code: "", type: "percent", value: "", description: "" });
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  const [gcalStatus, setGcalStatus] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { nav("/admin"); return; }
    Promise.all([
      api.get("/admin/stats", { headers }),
      api.get("/admin/bookings", { headers }),
      api.get("/admin/messages", { headers }),
      api.get("/admin/coupons", { headers }),
      api.get("/admin/feedback", { headers }),
      api.get("/auth/google/status", { headers }),
    ]).then(([s, b, m, c, f, g]) => {
      setStats(s.data); setBookings(b.data); setMessages(m.data); setCoupons(c.data); setFeedback(f.data);
      setGcalStatus(g.data);
    }).catch(() => { localStorage.removeItem("admin_token"); nav("/admin"); });
  }, []); // eslint-disable-line

  // Show toast on Google Calendar OAuth callback
  useEffect(() => {
    const g = searchParams.get("gcal");
    const reason = searchParams.get("reason");
    if (!g) return;
    if (g === "connected") {
      toast.success("Google Calendar connected");
    } else if (g === "state_mismatch") {
      toast.error("Google Calendar connection failed: state mismatch — try again", { duration: 12000 });
    } else if (reason === "access_denied") {
      toast.error("Google blocked the connection. Add 1motionelitetransport@gmail.com as a Test User in Google Cloud Console → OAuth consent screen → Test users.", { duration: 15000 });
    } else if (reason === "no_refresh_token") {
      toast.error("Google didn't return a refresh token (this happens when you previously authorized the app). Revoke access at https://myaccount.google.com/permissions → search '1Motion Elite Transport' → Remove access. Then click Connect again.", { duration: 30000 });
    } else if (reason) {
      toast.error(`Google Calendar connection failed: ${reason}`, { duration: 12000 });
    } else {
      toast.error(`Google Calendar connection ${g}`, { duration: 10000 });
    }
    // clear query string
    setSearchParams({}, { replace: true });
    // refresh gcal status
    api.get("/auth/google/status", { headers }).then(r => setGcalStatus(r.data)).catch(()=>{});
  }, [searchParams]); // eslint-disable-line

  const connectGCal = () => {
    window.location.href = `${api.defaults.baseURL}/auth/google/start?token=${encodeURIComponent(token)}`;
  };

  const disconnectGCal = async () => {
    if (!window.confirm("Disconnect Google Calendar? New bookings will no longer be added to your calendar.")) return;
    try {
      await api.post("/auth/google/disconnect", {}, { headers });
      setGcalStatus(s => ({ ...(s||{}), connected: false }));
      toast.success("Google Calendar disconnected");
    } catch { toast.error("Failed to disconnect"); }
  };

  const backfillGCal = async () => {
    if (!window.confirm("Sync existing paid bookings to your Google Calendar? This creates one event per paid, non-cancelled booking that isn't already on the calendar.")) return;
    const p = api.post("/admin/gcal/backfill", {}, { headers });
    toast.promise(p, {
      loading: "Syncing bookings to Google Calendar...",
      success: (r) => `Synced ${r.data.synced} booking${r.data.synced===1?"":"s"} to your calendar${r.data.failed?` (${r.data.failed} failed)`:""}`,
      error: (e) => e?.response?.data?.detail || "Backfill failed — try again",
    });
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}?status=${status}`, {}, { headers });
      setBookings(b => b.map(x => x.id === id ? {...x, status} : x));
      toast.success(`Marked ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const createCoupon = async (e) => {
    e.preventDefault();
    if (!newCoupon.code || !newCoupon.value) { toast.error("Code and value are required"); return; }
    try {
      const r = await api.post("/admin/coupons", { ...newCoupon, value: Number(newCoupon.value) }, { headers });
      setCoupons(c => [r.data, ...c]);
      setNewCoupon({ code: "", type: "percent", value: "", description: "" });
      toast.success(`Coupon ${r.data.code} created`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to create coupon"); }
  };

  const toggleCoupon = async (id, active) => {
    try {
      await api.patch(`/admin/coupons/${id}?active=${active}`, {}, { headers });
      setCoupons(c => c.map(x => x.id === id ? { ...x, active } : x));
      toast.success(active ? "Activated" : "Disabled");
    } catch { toast.error("Update failed"); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    try {
      await api.delete(`/admin/coupons/${id}`, { headers });
      setCoupons(c => c.filter(x => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const toggleFeedback = async (id, approved) => {
    try {
      await api.patch(`/admin/feedback/${id}?approved=${approved}`, {}, { headers });
      setFeedback(f => f.map(x => x.id === id ? { ...x, approved } : x));
      toast.success(approved ? "Published to homepage" : "Hidden");
    } catch { toast.error("Update failed"); }
  };

  const deleteFeedback = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/admin/feedback/${id}`, { headers });
      setFeedback(f => f.filter(x => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const logout = () => { localStorage.removeItem("admin_token"); nav("/admin"); };

  const cards = [
    { label: "Total Bookings", value: stats?.total_bookings ?? "—", Icon: Calendar },
    { label: "Confirmed",      value: stats?.confirmed ?? "—",      Icon: CheckCircle },
    { label: "Pending",        value: stats?.pending ?? "—",        Icon: Clock },
    { label: "Revenue",        value: stats ? `$${stats.revenue.toFixed(2)}` : "—", Icon: DollarSign },
  ];

  return (
    <div data-testid="admin-dashboard">
      <Navbar/>
      <div className="pt-32 pb-20 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
          <div>
            <p className="eyebrow mb-2">Dashboard</p>
            <h1 className="font-serif text-4xl">Operations</h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {gcalStatus?.configured && (
              gcalStatus?.connected ? (
                <>
                  <button
                    data-testid="gcal-backfill-btn"
                    onClick={backfillGCal}
                    className="btn-ghost text-[#f0f6fb] border-[#5DCFE5]/40"
                    title="Add all existing paid bookings to your Google Calendar"
                  >
                    <CalendarIcon size={14}/> Sync Bookings
                  </button>
                  <button
                    data-testid="gcal-disconnect-btn"
                    onClick={disconnectGCal}
                    className="btn-ghost text-[#5DCFE5] border-[#5DCFE5]/40"
                    title="Google Calendar is connected"
                  >
                    <CalendarIcon size={14}/> Calendar Connected
                  </button>
                </>
              ) : (
                <button
                  data-testid="gcal-connect-btn"
                  onClick={connectGCal}
                  className="btn-ghost text-[#f0f6fb] border-[#5DCFE5]/40"
                  title="Connect Google Calendar to enable double-booking prevention and calendar events"
                >
                  <CalendarIcon size={14}/> Connect Google Calendar
                </button>
              )
            )}
            <button onClick={logout} className="btn-ghost"><LogOut size={14}/> Logout</button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {cards.map(({ label, value, Icon }) => (
            <div key={label} className="card-dark p-6">
              <Icon size={18} className="text-[#5DCFE5] mb-3"/>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b7c8a] mb-1">{label}</div>
              <div className="font-serif text-3xl">{value}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          <button onClick={() => setTab("bookings")} data-testid="tab-bookings" className={`px-5 py-2 rounded-full text-xs tracking-[0.2em] uppercase ${tab==="bookings"?"bg-[#5DCFE5] text-[#050a12]":"border border-[#1a2838] text-[#6b7c8a]"}`}>Bookings</button>
          <button onClick={() => setTab("messages")} data-testid="tab-messages" className={`px-5 py-2 rounded-full text-xs tracking-[0.2em] uppercase ${tab==="messages"?"bg-[#5DCFE5] text-[#050a12]":"border border-[#1a2838] text-[#6b7c8a]"}`}>Messages</button>
          <button onClick={() => setTab("coupons")} data-testid="tab-coupons" className={`px-5 py-2 rounded-full text-xs tracking-[0.2em] uppercase ${tab==="coupons"?"bg-[#5DCFE5] text-[#050a12]":"border border-[#1a2838] text-[#6b7c8a]"}`}>Coupons</button>
        </div>

        {tab === "bookings" && (
          <div className="card-dark overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#07101a] text-[#6b7c8a]">
                  <tr>
                    {["Customer","Package","Date/Time","Total","Payment","Status","Action"].map(h => (
                      <th key={h} className="text-left py-3 px-4 text-[10px] tracking-[0.2em] uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 && (<tr><td colSpan={7} className="text-center py-10 text-[#4a5868]">No bookings yet</td></tr>)}
                  {bookings.map(b => {
                    const isOpen = expandedBookingId === b.id;
                    return (
                      <Fragment key={b.id}>
                    <tr
                      data-testid={`booking-row-${b.id}`}
                      onClick={() => setExpandedBookingId(isOpen ? null : b.id)}
                      className={`border-t border-[#1a2838] cursor-pointer transition ${isOpen ? "bg-[#0a1520]" : "hover:bg-[#0a1520]/60"}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <ChevronDown size={12} className={`text-[#5DCFE5] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          <div>
                            <div className="text-[#f0f6fb]">{b.customer_name}</div>
                            <div className="text-xs text-[#6b7c8a]">{b.customer_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-[#b8c5d1]">{b.package_id}</td>
                      <td className="py-3 px-4 text-[#b8c5d1]">{b.pickup_date} {b.pickup_time}</td>
                      <td className="py-3 px-4 text-[#5DCFE5] font-bold">${b.total_amount.toFixed(2)}</td>
                      <td className="py-3 px-4"><span className={`px-2 py-1 rounded text-[10px] uppercase ${b.payment_status==="paid"?"bg-green-900/40 text-green-300":"bg-[#1a2838] text-[#6b7c8a]"}`}>{b.payment_status}</span></td>
                      <td className="py-3 px-4"><span className="text-xs">{b.status}</span></td>
                      <td className="py-3 px-4" onClick={(e)=>e.stopPropagation()}>
                        <select value={b.status} onChange={e=>setStatus(b.id, e.target.value)} className="bg-[#07101a] border border-[#1a2838] rounded px-2 py-1 text-xs">
                          <option value="pending">pending</option>
                          <option value="confirmed">confirmed</option>
                          <option value="completed">completed</option>
                          <option value="cancelled">cancelled</option>
                        </select>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${b.id}-details`} data-testid={`booking-details-${b.id}`} className="border-t border-[#1a2838] bg-[#050d16]">
                        <td colSpan={7} className="px-6 py-5">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                            <div>
                              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b7c8a] mb-1">Phone</div>
                              <a href={`tel:${b.customer_phone||""}`} className="text-[#5DCFE5]">{b.customer_phone || "—"}</a>
                            </div>
                            <div>
                              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b7c8a] mb-1">Passengers</div>
                              <div className="text-[#f0f6fb]">{b.passengers ?? "—"}</div>
                            </div>
                            <div>
                              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b7c8a] mb-1">Payment Method</div>
                              <div className="text-[#f0f6fb] uppercase">{b.payment_method || "—"}</div>
                            </div>
                            <div className="md:col-span-3">
                              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b7c8a] mb-1">Pickup Address</div>
                              <div className="text-[#f0f6fb]">{b.pickup_address || "—"}</div>
                            </div>
                            <div className="md:col-span-3">
                              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b7c8a] mb-1">Drop-off Address</div>
                              <div className="text-[#f0f6fb]">{b.dropoff_address || (b.hours ? "(round trip / hourly)" : "—")}</div>
                            </div>
                            {b.distance_miles ? (
                              <div>
                                <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b7c8a] mb-1">Distance</div>
                                <div className="text-[#f0f6fb]">{b.distance_miles} mi</div>
                              </div>
                            ) : null}
                            {b.hours ? (
                              <div>
                                <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b7c8a] mb-1">Hours</div>
                                <div className="text-[#f0f6fb]">{b.hours} hrs</div>
                              </div>
                            ) : null}
                            {b.addons && b.addons.length > 0 && (
                              <div className="md:col-span-3">
                                <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b7c8a] mb-1">Add-ons</div>
                                <div className="text-[#f0f6fb]">{b.addons.join(", ")}</div>
                              </div>
                            )}
                            {b.coupon_code && (
                              <div>
                                <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b7c8a] mb-1">Coupon</div>
                                <div className="text-[#5DCFE5]">{b.coupon_code} (−${(b.discount_amount||0).toFixed(2)})</div>
                              </div>
                            )}
                            {b.notes && (
                              <div className="md:col-span-3">
                                <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b7c8a] mb-1">Notes</div>
                                <div className="text-[#f0f6fb] whitespace-pre-wrap">{b.notes}</div>
                              </div>
                            )}
                            <div className="md:col-span-3 pt-2 border-t border-[#1a2838] text-xs text-[#4a5868]">
                              Booking ID: <span className="text-[#6b7c8a] font-mono">{b.id}</span>
                              {b.created_at && <> · Created: {new Date(b.created_at).toLocaleString()}</>}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-3">
            {messages.length === 0 && <div className="card-dark p-10 text-center text-[#4a5868]">No messages yet</div>}
            {messages.map(m => (
              <div key={m.id} className="card-dark p-5">
                <div className="flex justify-between mb-2">
                  <div className="font-medium">{m.name} <span className="text-[#6b7c8a] text-xs">· {m.email}</span></div>
                  <div className="text-xs text-[#4a5868]">{new Date(m.created_at).toLocaleString()}</div>
                </div>
                <p className="text-[#b8c5d1] text-sm">{m.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "coupons" && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Create coupon */}
            <form onSubmit={createCoupon} className="card-dark p-6 lg:col-span-1 space-y-3">
              <p className="eyebrow mb-2">Create New Coupon</p>
              <input data-testid="coupon-code" required placeholder="CODE (e.g. SUMMER20)" value={newCoupon.code} onChange={e => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})} className="input-dark uppercase tracking-widest"/>
              <select value={newCoupon.type} onChange={e => setNewCoupon({...newCoupon, type: e.target.value})} className="input-dark">
                <option value="percent">Percentage off</option>
                <option value="fixed">Fixed $ off</option>
              </select>
              <input data-testid="coupon-value" required type="number" step="0.01" min="0" placeholder={newCoupon.type === "percent" ? "Percent (e.g. 10)" : "Dollar amount"} value={newCoupon.value} onChange={e => setNewCoupon({...newCoupon, value: e.target.value})} className="input-dark"/>
              <input placeholder="Description (optional)" value={newCoupon.description} onChange={e => setNewCoupon({...newCoupon, description: e.target.value})} className="input-dark"/>
              <button data-testid="create-coupon-btn" type="submit" className="btn-gold w-full justify-center">Create Coupon</button>
            </form>

            {/* List */}
            <div className="lg:col-span-2 space-y-3">
              {coupons.length === 0 && <div className="card-dark p-10 text-center text-[#4a5868]">No coupons yet — create your first one</div>}
              {coupons.map(c => (
                <div key={c.id} data-testid={`coupon-${c.code}`} className={`card-dark p-5 flex items-center justify-between gap-4 ${!c.active ? "opacity-50" : ""}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-mono text-[#5DCFE5] font-bold tracking-wider">{c.code}</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-[#1a2838] text-[#b8c5d1]">
                        {c.type === "percent" ? `${c.value}% off` : `$${c.value} off`}
                      </span>
                      {!c.active && <span className="text-xs text-red-300">disabled</span>}
                    </div>
                    {c.description && <div className="text-xs text-[#6b7c8a]">{c.description}</div>}
                    <div className="text-[10px] text-[#4a5868] mt-1 tracking-widest uppercase">Used {c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</div>
                  </div>
                  <button onClick={() => toggleCoupon(c.id, !c.active)} className="text-xs uppercase tracking-widest text-[#6b7c8a] hover:text-[#5DCFE5] transition">
                    {c.active ? "Disable" : "Activate"}
                  </button>
                  <button onClick={() => deleteCoupon(c.id)} className="text-xs uppercase tracking-widest text-[#6b7c8a] hover:text-red-300 transition">
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div className="space-y-3">
            {feedback.length === 0 && <div className="card-dark p-10 text-center text-[#4a5868]">No reviews yet — they&apos;ll appear here after customers leave feedback</div>}
            {feedback.map(f => (
              <div key={f.id} data-testid={`review-${f.id}`} className={`card-dark p-6 ${f.approved ? "border-[#5DCFE5]/40" : ""}`}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-medium">{f.customer_name}</span>
                      <div className="flex">
                        {[...Array(5)].map((_,i) => (
                          <span key={i} className={i < f.rating ? "text-[#5DCFE5]" : "text-[#1a2838]"}>★</span>
                        ))}
                      </div>
                      {f.tip_paid && <span className="text-[10px] text-[#5DCFE5] uppercase tracking-widest">Tipped ${f.tip_amount.toFixed(0)}</span>}
                      {f.approved ? (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-[#5DCFE5]/15 text-[#5DCFE5] uppercase tracking-widest">Published</span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-[#1a2838] text-[#6b7c8a] uppercase tracking-widest">Pending</span>
                      )}
                    </div>
                    <div className="text-[10px] text-[#4a5868] tracking-widest uppercase">{new Date(f.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => toggleFeedback(f.id, !f.approved)} data-testid={`approve-${f.id}`}
                      className="text-xs uppercase tracking-widest text-[#6b7c8a] hover:text-[#5DCFE5] transition">
                      {f.approved ? "Unpublish" : "Publish"}
                    </button>
                    <button onClick={() => deleteFeedback(f.id)} className="text-xs uppercase tracking-widest text-[#6b7c8a] hover:text-red-300 transition">Delete</button>
                  </div>
                </div>
                {f.comment && <p className="text-[#b8c5d1] text-sm italic leading-relaxed">&quot;{f.comment}&quot;</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
